// User(user.js, 非推奨)/User2(user2.ts)のどちらを渡しても使えるよう、
// 必要なフィールドのみを構造的に定義する
interface BugsnagUser {
  id: number;
  name: string;
  email?: string;
}

interface VersionMeta {
  commit?: string;
  latestPr?: string;
  prUrl?: string;
  buildTime?: string;
  commitMessage?: string;
  version?: string;
}

interface BugsnagReport {
  updateMetaData(section: string, values: object): void;
}

const getVersionMeta = (): VersionMeta | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const version = window.APP_VERSION;
  if (!version || typeof version !== 'object') {
    return undefined;
  }

  const { commit, latestPr, prUrl, buildTime, commitMessage, version: versionHash } = version;

  if (commit || latestPr || prUrl || buildTime || commitMessage || versionHash) {
    return { commit, latestPr, prUrl, buildTime, commitMessage, version: versionHash };
  }

  return undefined;
};

const addVersionMeta = (report: BugsnagReport) => {
  const versionMeta = getVersionMeta();
  if (versionMeta) {
    report.updateMetaData('version', versionMeta);
  }
};

export const setupBugsnag = (user: BugsnagUser) => {
  if (typeof bugsnagClient === 'undefined') {
    return;
  }

  bugsnagClient.user = {
    id: user.id,
    name: user.name,
    email: user.email
  };
};

type HttpErrorLike = Error & {
  request?: { url: string };
  response?: { status: number };
};

const isHttpErrorLike = (e: unknown): e is HttpErrorLike =>
  e instanceof Error && 'request' in e && typeof (e as HttpErrorLike).request?.url === 'string';

export const shouldReportErrorToBugsnag = (e: unknown): boolean => {
  if (!isHttpErrorLike(e)) {
    return true;
  }

  const status = e.response?.status;
  if (status === undefined) {
    return false;
  }

  return status >= 500;
};

export const reportError = (e: unknown) => {
  if (typeof bugsnagClient === 'undefined') {
    return;
  }

  if (!shouldReportErrorToBugsnag(e)) {
    return;
  }

  if (e instanceof Error) {
    bugsnagClient.notify(e, {
      beforeSend: addVersionMeta,
      severity: 'error'
    });
    return;
  }

  let errorObj: Error;
  let originalErrorMeta: Record<string, unknown> = {};

  if (e !== null && typeof e === 'object') {
    errorObj = new Error('Non-Error object was thrown');
    originalErrorMeta = {
      originalErrorType: 'object',
      originalErrorValue: e
    };
  } else if (typeof e === 'string') {
    errorObj = new Error(e);
    originalErrorMeta = {
      originalErrorType: 'string'
    };
  } else {
    errorObj = new Error(`Non-Error value was thrown: ${String(e)}`);
    originalErrorMeta = {
      originalErrorType: typeof e,
      originalErrorValue: e
    };
  }

  bugsnagClient.notify(errorObj, {
    beforeSend: (report: BugsnagReport) => {
      addVersionMeta(report);
      report.updateMetaData('originalError', originalErrorMeta);
    },
    severity: 'error'
  });
};
